import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const http = () => axios.create({
  baseURL: '/api',
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const VISA_SUBCLASSES = [
  { id: '500', label: 'Student Visa (Subclass 500)', desc: 'Study + limited work rights' },
  { id: '485', label: 'Temporary Graduate Visa (Subclass 485)', desc: 'Post-study work rights' },
  { id: '417', label: 'Working Holiday Visa (Subclass 417)', desc: 'Work & travel — 6 month per employer' },
  { id: '462', label: 'Work & Holiday Visa (Subclass 462)', desc: 'Work & travel — country-specific' },
  { id: '482', label: 'Temporary Skill Shortage Visa (Subclass 482)', desc: 'Employer-sponsored work' },
  { id: '494', label: 'Skilled Employer Sponsored Regional (Subclass 494)', desc: 'Regional employer-sponsored' },
  { id: '491', label: 'Skilled Work Regional (Subclass 491)', desc: 'Regional skilled migration' },
  { id: '189', label: 'Skilled Independent Visa (Subclass 189)', desc: 'Permanent — points-based' },
  { id: '190', label: 'Skilled Nominated Visa (Subclass 190)', desc: 'Permanent — state-nominated' },
  { id: '820', label: 'Partner Visa (Subclass 820/801)', desc: 'Onshore partner pathway' },
  { id: '309', label: 'Partner Visa (Subclass 309/100)', desc: 'Offshore partner pathway' },
  { id: '403', label: 'International Relations Visa (Subclass 403)', desc: 'Seasonal Worker Programme (PALM)' },
  { id: '408', label: 'Temporary Activity Visa (Subclass 408)', desc: 'Specialised short-term work' },
  { id: '600', label: 'Visitor Visa (Subclass 600)', desc: 'Tourist — no work rights' },
  { id: 'BVA', label: 'Bridging Visa A (BVA)', desc: 'While awaiting substantive visa' },
  { id: 'BVB', label: 'Bridging Visa B (BVB)', desc: 'Travel while on BVA' },
  { id: 'BVC', label: 'Bridging Visa C (BVC)', desc: 'After visa application lodged' },
  { id: 'other', label: 'Other / Not sure', desc: 'Different visa type' },
];

const WORK_HOURS = [
  { id: 'unrestricted', label: 'Unrestricted work rights', desc: 'Can work any hours' },
  { id: '48_per_fortnight', label: '48 hours per fortnight', desc: 'Student visa holders' },
  { id: '6_month_per_employer', label: '6 months per employer', desc: 'Working Holiday (417)' },
  { id: 'employer_nominated', label: 'Employer-nominated only', desc: '482/494 sponsor bound' },
  { id: 'no_work', label: 'No work rights', desc: 'Visitor visa / certain conditions' },
];

const VISA_ADVICE = {
  '500': {
    risk: 'medium', color: 'amber',
    tips: [
      'Must maintain enrolment and attendance',
      'Work limited to 48 hours per fortnight while course is in session',
      'Unlimited hours during scheduled course breaks',
      'Must maintain OSHC health insurance',
      'Notify provider if you change address within 7 days',
    ],
    links: [
      { label: 'Understand your student visa conditions', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/student-500' },
      { label: 'Learn more about work rights for students', url: 'https://immi.homeaffairs.gov.au/visas/already-have-a-visa/check-visa-details-and-conditions/see-work-conditions' },
    ],
  },
  '485': {
    risk: 'high', color: 'rose',
    tips: [
      'Temporary visa — plan for transition to skilled (189/190/491) before expiry',
      'You can work unrestricted hours',
      'Post-study work period depends on qualification level',
      'Consider skills assessment for permanent pathway',
    ],
    links: [
      { label: 'Graduate visa conditions', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/temporary-graduate-485' },
      { label: 'Check skills assessment requirements', url: 'https://www.qualificationsrecognition.com.au/' },
    ],
  },
  '417': {
    risk: 'medium', color: 'amber',
    tips: [
      'Max 6 months with any single employer',
      'Need specified work for second or third year extension',
      'Specified work includes hospitality, agriculture, construction in regional areas',
      'You can study for up to 4 months',
    ],
    links: [
      { label: 'WHV 417 conditions', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/working-holiday-417' },
    ],
  },
  '462': {
    risk: 'medium', color: 'amber',
    tips: [
      'Max 6 months with any single employer',
      'Need specified work for second or third year extension',
      'Specified work can be hospitality, agriculture, mining, construction in regional areas',
      'You can study for up to 4 months',
      'Country-specific caps apply per year',
    ],
    links: [
      { label: 'Work and Holiday 462 conditions', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/work-holiday-462' },
    ],
  },
  '482': {
    risk: 'high', color: 'rose',
    tips: [
      'Tied to your sponsoring employer — changing employers requires new sponsorship',
      'Work only in your nominated occupation',
      'Ensure sponsor is meeting all obligations (market salary rate)',
      'Pathway to PR via 186 (Temporary Residence Transition)',
    ],
    links: [
      { label: '482 visa conditions', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/temporary-skill-shortage-482' },
      { label: 'Sponsor obligations explained', url: 'https://immi.homeaffairs.gov.au/help-support/employer-sponsorship' },
    ],
  },
  '494': {
    risk: 'high', color: 'rose',
    tips: [
      'Regional employer-sponsored visa — must live and work in regional Australia',
      'Pathway to PR after 3 years in regional area (Subclass 191)',
      'Your employer must be an approved regional sponsor',
      'Occupations are limited to those on the regional occupation list',
    ],
    links: [
      { label: '494 visa overview', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/skilled-employer-sponsored-regional-494' },
    ],
  },
  '491': {
    risk: 'low', color: 'emerald',
    tips: [
      'Regional provisional visa — pathway to PR after 3 years',
      'Must live and work in a designated regional area',
      'Requires nomination by state/territory or family member sponsor',
      'Can work for any employer in any occupation',
    ],
    links: [
      { label: '491 visa conditions', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/skilled-work-regional-491' },
    ],
  },
  '189': {
    risk: 'low', color: 'emerald',
    tips: [
      'Permanent residency — live and work anywhere in Australia',
      'No work restrictions or employer sponsorship required',
      'Eligible for Medicare, Centrelink, and citizenship pathway',
      'Must have been invited via the SkillSelect EOI system',
    ],
    links: [
      { label: 'Skilled Independent 189', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/skilled-independent-189' },
    ],
  },
  '190': {
    risk: 'low', color: 'emerald',
    tips: [
      'Permanent residency with state/territory nomination',
      'Must live in the nominating state for at least 2 years',
      'No work restrictions — you can work in any occupation',
      'Eligible for Medicare, Centrelink, and citizenship pathway',
    ],
    links: [
      { label: 'Skilled Nominated 190', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/skilled-nominated-190' },
    ],
  },
  '820': {
    risk: 'low', color: 'emerald',
    tips: [
      'Onshore partner visa — bridging visa A usually granted while processing',
      'You can work unrestricted while this visa is being processed',
      'Medicare eligibility usually applies',
      'Permanent stage (801) assessed 2 years after application',
    ],
    links: [
      { label: 'Partner visa (onshore) 820/801', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/partner-onshore-820-801' },
    ],
  },
  '309': {
    risk: 'low', color: 'emerald',
    tips: [
      'Offshore partner visa — you apply from outside Australia',
      'Provisional stage (309) grants travel to Australia',
      'Permanent stage (100) assessed after 2 years',
      'Check processing times — can take 12-24 months',
    ],
    links: [
      { label: 'Partner visa (offshore) 309/100', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/partner-offshore-309-100' },
    ],
  },
  '403': {
    risk: 'medium', color: 'amber',
    tips: [
      'Seasonal Worker Programme (Pacific Australia Labour Mobility)',
      'Work in specified sectors: agriculture, meat processing, aged care, hospitality',
      'Your employer must be an approved PALM scheme sponsor',
      'Can be in Australia for up to 3 years depending on industry',
    ],
    links: [
      { label: 'International Relations 403', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/international-relations-403' },
      { label: 'PALM scheme employer obligations', url: 'https://www.palmscheme.gov.au/' },
    ],
  },
  '408': {
    risk: 'medium', color: 'amber',
    tips: [
      'Temporary Activity Visa — covers specialised short-term work',
      'May be used for COVID-19 pandemic event work (no longer current)',
      'Requires a sponsor or approved organisation',
      'Work rights depend on the specific activity category',
    ],
    links: [
      { label: 'Temporary Activity 408', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/temporary-activity-408' },
    ],
  },
  '600': {
    risk: 'info', color: 'blue',
    tips: [
      'Visitor visa — employment is NOT permitted',
      'Can study for up to 3 months',
      'Must not work, even unpaid, in most circumstances',
      'Ensure you hold the correct visa if you intend to work',
    ],
    links: [
      { label: 'Visitor visa 600 conditions', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/visitor-600' },
    ],
  },
};

const STATUS_MAP = {
  '500': { status: 'Warning', color: 'amber', label: 'Limited work rights' },
  '485': { status: 'Expiring', color: 'rose', label: 'Temporary visa — act now' },
  '417': { status: 'Active', color: 'emerald', label: 'Active with conditions' },
  '462': { status: 'Active', color: 'emerald', label: 'Active with conditions' },
  '482': { status: 'Limited', color: 'rose', label: 'Sponsor-dependent' },
  '494': { status: 'Limited', color: 'rose', label: 'Sponsor-dependent' },
  '491': { status: 'Provisional', color: 'amber', label: 'Pathway to PR' },
  '189': { status: 'Permanent', color: 'emerald', label: 'No restrictions' },
  '190': { status: 'Permanent', color: 'emerald', label: 'No restrictions' },
  '820': { status: 'Processing', color: 'amber', label: 'Bridging while processing' },
  '309': { status: 'Processing', color: 'amber', label: 'Offshore — processing' },
  '403': { status: 'Active', color: 'emerald', label: 'PALM scheme worker' },
  '408': { status: 'Active', color: 'emerald', label: 'Limited activity visa' },
  '600': { status: 'No Work', color: 'red', label: 'Cannot work' },
  'BVA': { status: 'Bridging', color: 'blue', label: 'Awaiting new visa' },
  'BVB': { status: 'Bridging', color: 'blue', label: 'Travel while bridging' },
  'BVC': { status: 'Bridging', color: 'blue', label: 'Lodged application' },
  'other': { status: 'Unknown', color: 'neutral', label: 'Check conditions' },
};

// ─── SVG Icons ───────────────────────────────────────────────
function CalendarIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

export default function VisaAgentPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [reminders, setReminders] = useState([]);

  const [form, setForm] = useState({
    visa_subclass: '',
    visa_expiry_date: '',
    work_hours_limit: 'unrestricted',
    has_bridging_visa: false,
  });

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await http().get('/visa/profile');
      if (res.data?.profile) {
        setProfile(res.data.profile);
        setForm({
          visa_subclass: res.data.profile.visa_subclass || '',
          visa_expiry_date: res.data.profile.visa_expiry_date
            ? new Date(res.data.profile.visa_expiry_date).toISOString().split('T')[0]
            : '',
          work_hours_limit: res.data.profile.work_hours_limit || 'unrestricted',
          has_bridging_visa: res.data.profile.has_bridging_visa || false,
        });
      }
      // Load reminders (non-critical, separate try/catch)
      try {
        const remRes = await http().get('/visa/reminders');
        if (remRes.data?.reminders) setReminders(remRes.data.reminders);
      } catch (remErr) {
        if (remErr.response?.status !== 401) {
          console.warn('Reminder fetch failed (non-critical):', remErr.message);
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      setError('Could not load your visa profile. Try refreshing.');
    }
    setLoading(false);
  };

  const saveProfile = async () => {
    if (!form.visa_subclass || !form.visa_expiry_date) {
      setError('Please select your visa type and expiry date');
      return;
    }
    const selectedDate = new Date(form.visa_expiry_date);
    const now = new Date();
    const maxPast = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
    if (selectedDate < maxPast) {
      setError('Please enter a valid visa expiry date');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const res = await http().post('/visa/profile', form);
      setProfile(res.data.profile);
      setShowForm(false);
      showToast("Visa saved! We'll remind you before it expires.");

      // Refresh reminders after save
      try {
        const remRes = await http().get('/visa/reminders');
        if (remRes.data?.reminders) setReminders(remRes.data.reminders);
      } catch (_) {}
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      setError(err.response?.data?.error || 'Failed to save visa profile');
    }
    setSaving(false);
  };

  const deleteProfile = async () => {
    if (!window.confirm('Remove your visa profile? You will stop receiving reminders.')) return;
    try {
      await http().delete('/visa/profile');
      setProfile(null);
      setReminders([]);
      showToast('Visa profile removed.');
    } catch (err) {
      setError('Failed to delete profile');
    }
  };

  const acknowledgeReminder = async (id) => {
    try {
      await http().post(`/visa/reminders/${id}/acknowledge`);
      setReminders(prev => prev.map(r => r.id === id ? { ...r, acknowledged: true } : r));
    } catch (_) {}
  };

  const handleAffiliateClick = async () => {
    try {
      window.open('https://www.migrationexpert.com.au/', '_blank', 'noopener');
      await http().get('/visa/migration-agent').catch(() => {});
    } catch {} // Silent — tracking is non-critical
  };

  // ─── Loading ────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-neutral-400">Loading your visa info...</p>
        </div>
      </div>
    );
  }

  const subclassInfo = VISA_SUBCLASSES.find(s => s.id === profile?.visa_subclass);
  const advice = VISA_ADVICE[profile?.visa_subclass];
  const statusInfo = STATUS_MAP[profile?.visa_subclass] || { status: 'Unknown', color: 'neutral', label: 'Check conditions' };
  const daysLeft = profile?.days_until_expiry;
  const isOverdue = daysLeft !== null && daysLeft <= 0;
  const isUrgent = daysLeft !== null && daysLeft > 0 && daysLeft <= 90;
  const isCritical = daysLeft !== null && daysLeft > 0 && daysLeft <= 30;

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Toast notification */}
        {toast && (
          <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
            toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'
          }`}>
            {toast.msg}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/worker" className="text-neutral-400 hover:text-brand-600 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">🛂 Visa Agent</h1>
            <p className="text-sm text-neutral-500">Free AI-powered visa monitoring</p>
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-700 mb-6 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-rose-400 hover:text-rose-600 ml-2 text-lg leading-none">&times;</button>
          </div>
        )}

        {!profile ? (
          /* ─── No profile — show CTA ────────────────── */
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-brand-500 to-brand-700 rounded-2xl p-6 sm:p-8 text-white">
              <div className="text-5xl mb-4">🛂</div>
              <h2 className="text-2xl font-bold mb-2">Track your visa for free</h2>
              <p className="text-brand-100 text-sm mb-6 max-w-md">
                Know exactly when your visa expires, your work rights, and when to renew.
                Free reminders so you never miss a deadline.
              </p>
              <button onClick={() => setShowForm(true)}
                className="px-6 py-3 bg-white text-brand-700 font-bold rounded-xl hover:bg-brand-50 transition shadow-lg active:scale-[0.98]">
                Set up your visa profile →
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-neutral-100 p-4">
                <div className="w-10 h-10 bg-brand-50 rounded-xl flex items-center justify-center mb-3"><span className="text-xl">⏰</span></div>
                <h3 className="font-semibold text-sm text-neutral-900 mb-1">Automatic alerts</h3>
                <p className="text-xs text-neutral-500">Get reminded at 90, 60, 30, 14, 7, and 1 day before expiry</p>
              </div>
              <div className="bg-white rounded-xl border border-neutral-100 p-4">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-3"><span className="text-xl">📋</span></div>
                <h3 className="font-semibold text-sm text-neutral-900 mb-1">Know your conditions</h3>
                <p className="text-xs text-neutral-500">Track work hours limits, sponsorship obligations, bridging visa status</p>
              </div>
              <div className="bg-white rounded-xl border border-neutral-100 p-4">
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center mb-3"><span className="text-xl">🤝</span></div>
                <h3 className="font-semibold text-sm text-neutral-900 mb-1">Expert help when needed</h3>
                <p className="text-xs text-neutral-500">Connected to MARA-registered migration agents for visa renewals</p>
              </div>
            </div>

            {showForm && (
              <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6">
                <h3 className="font-bold text-lg text-neutral-900 mb-4">Your Visa Details</h3>
                <VisaForm form={form} setForm={setForm} saving={saving} onSave={saveProfile} onCancel={() => setShowForm(false)} />
              </div>
            )}
          </div>
        ) : (
          /* ─── Have profile — show dashboard ──────── */
          <div className="space-y-4">
            {/* Status card */}
            <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm overflow-hidden">
              <div className={`p-5 ${
                isOverdue ? 'bg-red-50 border-b border-red-200' :
                isCritical ? 'bg-red-50 border-b border-red-100' :
                isUrgent ? 'bg-amber-50 border-b border-amber-100' :
                'bg-brand-50 border-b border-brand-100'
              }`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                        statusInfo.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
                        statusInfo.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                        statusInfo.color === 'rose' ? 'bg-rose-100 text-rose-700' :
                        statusInfo.color === 'red' ? 'bg-red-100 text-red-700' :
                        statusInfo.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                        'bg-neutral-100 text-neutral-600'
                      }`}>{statusInfo.status}</span>
                      {(isCritical || isOverdue) && (
                        <span className="px-2.5 py-0.5 bg-red-200 text-red-800 rounded-full text-xs font-bold animate-pulse">URGENT</span>
                      )}
                    </div>
                    <h2 className="text-xl font-bold text-neutral-900 mt-1">
                      {subclassInfo ? subclassInfo.label : 'Visa #' + profile.visa_subclass}
                    </h2>
                    <p className="text-sm text-neutral-500 mt-0.5">{subclassInfo?.desc || statusInfo.label}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {isOverdue ? (
                      <>
                        <div className="text-3xl font-extrabold text-red-600">⚠️</div>
                        <div className="text-xs text-red-500 font-semibold mt-0.5">OVERDUE</div>
                      </>
                    ) : (
                      <>
                        <div className={`text-4xl font-extrabold tabular-nums ${
                          isCritical ? 'text-red-600' :
                          isUrgent ? 'text-amber-600' :
                          'text-brand-600'
                        }`}>
                          {daysLeft}
                        </div>
                        <div className="text-xs text-neutral-400 mt-0.5">
                          {daysLeft === 1 ? 'day left' : 'days left'}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-5 space-y-4">
                {/* Expiry & work rights */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-neutral-50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 text-xs text-neutral-400 mb-1">
                      <CalendarIcon /> Expiry date
                    </div>
                    <p className="font-semibold text-neutral-900">
                      {profile.visa_expiry_date
                        ? new Date(profile.visa_expiry_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="bg-neutral-50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 text-xs text-neutral-400 mb-1">
                      <ShieldIcon /> Work rights
                    </div>
                    <p className="font-semibold text-neutral-900">
                      {WORK_HOURS.find(w => w.id === profile.work_hours_limit)?.label || 'Unrestricted'}
                    </p>
                  </div>
                </div>

                {/* Bridging visa */}
                {profile.has_bridging_visa && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-700">
                    ⏳ You have a bridging visa — you can stay lawfully while your new visa is processed.
                  </div>
                )}

                {/* Visa-specific tips */}
                {advice && advice.tips.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-neutral-900 mb-2">What you need to know</h4>
                    <ul className="space-y-1.5">
                      {advice.tips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-neutral-600">
                          <span className="text-brand-500 mt-0.5 flex-shrink-0">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                    {advice.links && advice.links.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {advice.links.map((link, i) => (
                          <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600 font-medium">
                            {link.label} <ExternalIcon />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Generic fallback */}
                {!advice && (
                  <div className="bg-neutral-50 rounded-xl p-4 text-sm text-neutral-600">
                    <p className="font-semibold text-neutral-700 mb-1">ℹ️ About your visa type</p>
                    <p>Check your specific visa conditions and work rights on the Department of Home Affairs website. Rules vary significantly by visa type.</p>
                    <div className="mt-2 space-y-1">
                      <a href="https://immi.homeaffairs.gov.au/visas/already-have-a-visa/check-visa-details-and-conditions/overview" target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600 font-medium">
                        Check your visa conditions ↗
                      </a>
                    </div>
                  </div>
                )}

                {/* Reminders section */}
                {reminders && reminders.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-neutral-900 mb-2">Upcoming reminders</h4>
                    <div className="space-y-2">
                      {reminders.filter(r => !r.acknowledged).map(r => {
                        const remDays = r.days_before;
                        const remText =
                          remDays === 90 ? '90 days before expiry — start planning renewal' :
                          remDays === 60 ? '60 days before expiry — time to act' :
                          remDays === 30 ? '30 days before expiry — urgent renewal needed' :
                          remDays === 14 ? '14 days before expiry — submit renewal now' :
                          remDays === 7 ? '7 days before expiry — last week' :
                          remDays === 1 ? '1 day before expiry — expires tomorrow' :
                          `${remDays} days before expiry`;
                        return (
                          <div key={r.id} className="flex items-center justify-between bg-white border border-neutral-100 rounded-xl px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{remDays <= 14 ? '🔴' : remDays <= 30 ? '🟡' : '🟢'}</span>
                              <div>
                                <p className="text-sm font-medium text-neutral-900">{remText}</p>
                                <p className="text-xs text-neutral-400">{r.visa_subclass} • {new Date(r.visa_expiry_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                              </div>
                            </div>
                            <button onClick={() => acknowledgeReminder(r.id)}
                              className="text-xs text-brand-500 hover:text-brand-600 font-medium whitespace-nowrap">
                              Dismiss
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    {reminders.filter(r => !r.acknowledged).length === 0 && (
                      <p className="text-xs text-neutral-400">All reminders acknowledged ✓</p>
                    )}
                  </div>
                )}

                {/* Official resources */}
                <details className="group">
                  <summary className="text-sm font-semibold text-neutral-600 cursor-pointer hover:text-brand-600 transition list-none flex items-center gap-2">
                    <span>Official Home Affairs resources</span>
                    <span className="text-neutral-300 group-open:rotate-180 transition-transform">▾</span>
                  </summary>
                  <div className="mt-3 space-y-1.5 pl-2">
                    <a href="https://vevo.homeaffairs.gov.au/" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700">
                      <ExternalIcon /> Check your visa online (VEVO)
                    </a>
                    <a href="https://immi.homeaffairs.gov.au/visas/already-have-a-visa/check-visa-details-and-conditions/overview" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700">
                      <ExternalIcon /> Full visa conditions guide
                    </a>
                    <a href="https://www.fairwork.gov.au/find-help-for/visa-holders-migrants" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700">
                      <ExternalIcon /> Fair Work — Migrant worker rights
                    </a>
                  </div>
                </details>
              </div>
            </div>

            {/* Migration Agent CTA */}
            <div className="bg-gradient-to-r from-brand-500 to-brand-700 rounded-2xl p-5 text-white">
              <div className="flex items-start gap-4">
                <div className="text-4xl">🤝</div>
                <div>
                  <h3 className="font-bold mb-1">Need visa help?</h3>
                  <p className="text-sm text-brand-100 mb-3">
                    Our partner migration agents (MARA-registered) can help with renewals,
                    new applications, and pathway to PR. Get expert advice.
                  </p>
                  <button onClick={() => handleAffiliateClick()}
                    className="px-5 py-2.5 bg-white text-brand-700 font-bold rounded-xl hover:bg-brand-50 transition text-sm shadow active:scale-[0.98]">
                    Talk to a migration agent →
                  </button>
                  <p className="text-[10px] text-brand-200 mt-2">
                    ShiftMatch may earn a commission when you use our partner agents
                  </p>
                </div>
              </div>
            </div>

            {/* Edit / Delete */}
            <div className="flex items-center gap-3">
              <button onClick={() => setShowForm(true)}
                className="flex-1 py-2.5 bg-white border border-neutral-200 text-neutral-700 font-semibold rounded-xl hover:bg-neutral-50 transition text-sm active:scale-[0.98]">
                Edit visa details
              </button>
              <button onClick={deleteProfile}
                className="px-4 py-2.5 text-rose-500 hover:text-rose-600 text-sm font-medium hover:bg-rose-50 rounded-xl transition">
                Remove
              </button>
            </div>

            {/* Edit form overlay */}
            {showForm && (
              <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6">
                <h3 className="font-bold text-lg text-neutral-900 mb-4">Edit Visa Details</h3>
                <VisaForm form={form} setForm={setForm} saving={saving} onSave={saveProfile} onCancel={() => setShowForm(false)} />
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-[10px] sm:text-xs text-neutral-300 leading-relaxed">
            ShiftMatch Visa Agent is a free tool and does not provide legal advice.
            Always consult a MARA-registered migration agent for official visa advice.
            🇦🇺
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Reusable form component ──────────────────────────────────
function VisaForm({ form, setForm, saving, onSave, onCancel }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Visa type</label>
        <select value={form.visa_subclass} onChange={e => setForm(f => ({ ...f, visa_subclass: e.target.value }))}
          className="w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500">
          <option value="">Select your visa...</option>
          {VISA_SUBCLASSES.map(v => (
            <option key={v.id} value={v.id}>{v.label}</option>
          ))}
        </select>
        {form.visa_subclass && (
          <p className="text-xs text-neutral-400 mt-1">
            {VISA_SUBCLASSES.find(v => v.id === form.visa_subclass)?.desc}
          </p>
        )}
      </div>
      <div>
        <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Visa expiry date</label>
        <input type="date" value={form.visa_expiry_date} onChange={e => setForm(f => ({ ...f, visa_expiry_date: e.target.value }))}
          className="w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Work rights</label>
        <select value={form.work_hours_limit} onChange={e => setForm(f => ({ ...f, work_hours_limit: e.target.value }))}
          className="w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500">
          {WORK_HOURS.map(w => (
            <option key={w.id} value={w.id}>{w.label}</option>
          ))}
        </select>
      </div>
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={form.has_bridging_visa} onChange={e => setForm(f => ({ ...f, has_bridging_visa: e.target.checked }))}
          className="w-4 h-4 rounded border-neutral-200 text-brand-500 focus:ring-brand-500" />
        <div>
          <span className="text-sm font-medium text-neutral-700">I have a bridging visa</span>
          <p className="text-xs text-neutral-400">A bridging visa lets you stay lawfully while waiting for a new visa decision</p>
        </div>
      </label>
      <div className="flex gap-3 pt-2">
        <button onClick={onCancel}
          className="flex-1 py-2.5 border border-neutral-200 text-neutral-600 font-semibold rounded-xl hover:bg-neutral-50 transition text-sm">
          Cancel
        </button>
        <button onClick={onSave} disabled={saving}
          className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl transition text-sm disabled:opacity-70">
          {saving ? 'Saving...' : 'Save visa profile'}
        </button>
      </div>
    </div>
  );
}
